import usePaginationStateHooks from 'hooks/CustomHooks/usePaginationStateHooks';
import useToggle from 'hooks/CustomHooks/useToggle';
import React, { useEffect, useState } from 'react';
import { useLoginStore } from 'store/useLoginDataStore';
import { BorderColor } from 'styles/Global/colorSemantic';
import { PageTitle, RowDivider } from 'styles/Global/commonStyles';
import { DiagnosisLayout, FilterWrapper, HospitalPageLabel } from 'styles/Global/hospitalStyles';
import AdminAssessmentPaymentPagination from './AdminAssessmentPaymentPagination';
import { useLoadingStore } from 'store/useLoadingStore';
import { userApi } from 'api/DCK/userApi';
import AssessmentPaymentModal from 'components/Admin/Modal/UserManageModal';
import { AdminAssessmentPaymentConstants } from 'constants/Admin/AdminAssessmentPaymentConstants';
import { GetUserListApiListType } from 'models/DCKAdmin/AdminManageUserJoinPageTypes';
import { AdminInnerLayout } from 'styles/Global/Admin/adminCommonStyles';
import { GetAssessmentPaymentApiListType } from 'models/Admin/AdminAssessmentPaymentTypes';
import { adminAssessmentPaymentPageApi } from 'api/Admin/adminAssessmentPaymentPageApi';
import AdminAssessmentPaymentFilter from './AdminAssessmentPaymentFilter';
import { useToastStore } from 'store/useToastStore';
import moment from 'moment';
import * as XLSX from 'xlsx';

const AdminAssessmentPaymentPage = () => {
    const { loginData } = useLoginStore();
    const { setLoading } = useLoadingStore();

    const [selectedSearchFilter, setselectedSearchFilter] = useState<number>(0);
    const [paymentData, setPaymentData] = useState<GetAssessmentPaymentApiListType[]>([]);
    const [searchValue, setSearchValue] = useState<string>('');
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [selectedUserData, setSelectedUserData] = useState<GetUserListApiListType | null>(null);

    // 날짜 필터링 관련 상태
    const [startDate, setStartDate] = useState<string | null>(moment().subtract(1, 'years').format('YYYY/MM/DD'));
    const [endDate, setEndDate] = useState<string | null>(moment().format('YYYY/MM/DD'));
    const [selectedPeriod, setSelectedPeriod] = useState<number>(0);
    const [selectSequence, setSelectSequence] = useState<string>('');
    const [beforeCalendarInputWarning, setBeforeCalendarInputWarning] = useState<boolean>(false);
    const [afterCalendarInputWarning, setAfterCalendarInputWarning] = useState<boolean>(false);
    const [afterCalenderDisabled, setAfterCalenderDisabled] = useState<boolean>(true);

    // 드롭다운 상태
    const [isCancelModal, setIsCancelModal] = useToggle();
    const [isProcessModal, setIsProcessModal] = useToggle();
    const [onSelectedPeriodDropdown, setOnSelectedPeriodDropdown] = useToggle();
    const [onSearchFilterDropdown, setOnSearchFilterDropdown] = useToggle();
    const [onBeforeCalendar, setOnBeforeCalendar] = useToggle();
    const [onAfterCalendar, setOnAfterCalendar] = useToggle();

    // 토스트 메시지
    const { toastToggle, comment } = useToastStore();

    const { setPage, currentPage, pageGroup, handlePageGroup } = usePaginationStateHooks(paymentData);

    useEffect(() => {
        adminAssessmentPaymentPageApiCall();
    }, [currentPage]);

    // selectedPeriod에 따라 날짜 설정
    useEffect(() => {
        switch (selectedPeriod) {
            case 0: // 1년
                setStartDate(moment().subtract(1, 'years').format('YYYY/MM/DD'));
                setEndDate(moment().format('YYYY/MM/DD'));
                break;
            case 1: // 1개월
                setStartDate(moment().subtract(1, 'months').format('YYYY/MM/DD'));
                setEndDate(moment().format('YYYY/MM/DD'));
                break;
            case 2: // 1주
                setStartDate(moment().subtract(1, 'weeks').format('YYYY/MM/DD'));
                setEndDate(moment().format('YYYY/MM/DD'));
                break;
            case 3: // 오늘
                setStartDate(moment().format('YYYY/MM/DD'));
                setEndDate(moment().format('YYYY/MM/DD'));
                break;
            case 4: // 직접 입력 - 수동으로 설정
                // 캘린더에서 직접 설정하므로 여기서는 아무것도 하지 않음
                break;
            default:
                break;
        }
    }, [selectedPeriod]);

    // search dropdown function
    const handleDropdown = (e: React.MouseEvent, idx: number, type?: string) => {
        e.stopPropagation();
        if (type === 'period') {
            setOnSelectedPeriodDropdown();
            setSelectedPeriod(idx);
            if (idx === 4) { // 직접 입력
                setAfterCalenderDisabled(false);
            } else {
                setAfterCalenderDisabled(true);
            }
        } else {
            setOnSearchFilterDropdown();
            setselectedSearchFilter(idx);
            setSearchValue('');
        }
        setPage(1);
    };

    // calendar dropdown function
    const handleCalendarDropdown = (type: string) => {
        if (type === 'before') {
            setOnBeforeCalendar();
        } else if (type === 'after') {
            setOnAfterCalendar();
        }
    };

    // search filter function
    const handleSearchFilter = () => {
        adminAssessmentPaymentPageApiCall();
    };

    // excel download function
    const handleExcelDownload = async () => {
        setLoading(true);
        try {
            // 날짜 포맷을 YYYYMMDD로 변환
            const formattedStartDate = startDate ? moment(startDate, 'YYYY/MM/DD').format('YYYYMMDD') : null;
            const formattedEndDate = endDate ? moment(endDate, 'YYYY/MM/DD').format('YYYYMMDD') : null;

            // 전체 데이터 가져오기 (page, limit 없이)
            const response = await adminAssessmentPaymentPageApi.getAssessmentPaymentListForExcel(
                searchValue || null,
                loginData.token,
                selectedSearchFilter,
                formattedStartDate,
                formattedEndDate
            );

            if (response.data && response.data.list) {
                // 엑셀 데이터 변환
                const excelData = response.data.list.map((item, index) => ({
                    '번호': index + 1,
                    '병원명': item.hospitalName || '',
                    '환자명': item.patientName || '',
                    '접수번호': item.userTestingNo || '',
                    '검사코드': item.assessmentCode || '',
                    '정가': item.retailPrice || 0,
                    '공급가': item.supplyPrice || 0,
                    '일시': item.paymentDateTime || '',
                    '테스트여부': item.isTest ? '테스트' : '운영'
                }));

                // 워크북 생성
                const workbook = XLSX.utils.book_new();
                const worksheet = XLSX.utils.json_to_sheet(excelData);

                // 열 너비 설정
                const columnWidths = [
                    { wch: 8 },   // 번호
                    { wch: 20 },  // 병원명
                    { wch: 15 },  // 환자명
                    { wch: 20 },  // 접수번호
                    { wch: 15 },  // 검사코드
                    { wch: 10 },  // 정가
                    { wch: 10 },  // 공급가
                    { wch: 20 },  // 일시
                    { wch: 12 }   // 테스트여부
                ];
                worksheet['!cols'] = columnWidths;

                // 워크시트를 워크북에 추가
                XLSX.utils.book_append_sheet(workbook, worksheet, '개별검사정산');

                // 파일 다운로드
                const fileName = `개별검사정산_${moment().format('YYYYMMDD_HHmmss')}.xlsx`;
                XLSX.writeFile(workbook, fileName);
            }
        } catch (error) {
            console.error('Excel download failed:', error);
        }
        setLoading(false);
    };

    // user state update func
    const handleUpdateUserState = async (userId: string, status: number, type: string) => {
        setLoading(true);
        try {
            const rqData = { _id: userId, status: status };
            const response = await userApi.userChangeStatus(rqData, loginData.token);

            if (response.status === 'ok') {
                // if (response.data.hospitalId === '677e22fe3d73c33a066fc54a') {
                //     const rqData: UserChangeLevelRqData = {
                //         _id: response.data.id,
                //         level: AccountLevel.externalCounselor,
                //     };
                //     await userApi.userChangeLevel(rqData, loginData.token);
                // }
                await adminAssessmentPaymentPageApiCall();
            } else {
                console.log(response);
            }
        } catch (error) {
            console.log(error);
        }

        handleModalFunc(type);
        setLoading(false);
    };

    // assessment payment list api call
    const adminAssessmentPaymentPageApiCall = async () => {
        setLoading(true);

        try {
            // 날짜 포맷을 YYYYMMDD로 변환
            const formattedStartDate = startDate ? moment(startDate, 'YYYY/MM/DD').format('YYYYMMDD') : null;
            const formattedEndDate = endDate ? moment(endDate, 'YYYY/MM/DD').format('YYYYMMDD') : null;

            const response = await adminAssessmentPaymentPageApi.getAssessmentPaymentList(
                currentPage, 
                searchValue || null, 
                loginData.token, 
                selectedSearchFilter,
                formattedStartDate,
                formattedEndDate,
            );

            if (response.data) {
                setPaymentData(response.data.list || []);
                setTotalPages(response.data.pagination?.totalPages || 0);
                setTotalCount(response.data.pagination?.totalCount || 0);
            } else {
                console.log(response);
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };

    // reset Button function
    const handleResetButton = async () => {
        setLoading(true);
        try {
            const oneYearAgo = moment().subtract(1, 'years').format('YYYY/MM/DD');
            const today = moment().format('YYYY/MM/DD');
            // 날짜 포맷을 YYYYMMDD로 변환
            const formattedOneYearAgo = moment().subtract(1, 'years').format('YYYYMMDD');
            const formattedToday = moment().format('YYYYMMDD');
            
            const response = await adminAssessmentPaymentPageApi.getAssessmentPaymentList(1, null, loginData.token, 0, formattedOneYearAgo, formattedToday);

            if (response.data) {
                setSearchValue('');
                setStartDate(oneYearAgo);
                setEndDate(today);
                setSelectedPeriod(0);
                setselectedSearchFilter(0);
                setPaymentData(response.data.list || []);
                setTotalPages(response.data.pagination?.totalPages || 0);
                setTotalCount(response.data.pagination?.totalCount || 0);
                setPage(1);
                handlePageGroup(0);
            } else {
                console.log(response);
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };

    // Modal toggle func
    const handleModalFunc = (type: string) => {
        if (type === 'process') {
            setIsProcessModal();
        } else {
            setIsCancelModal();
        }
    };

    return (
        <DiagnosisLayout>
            {isProcessModal && <AssessmentPaymentModal toggle={setIsProcessModal} selectedUserData={selectedUserData} type='process' onClick={handleUpdateUserState} />}
            {isCancelModal && <AssessmentPaymentModal toggle={setIsCancelModal} selectedUserData={selectedUserData} type='cancel' onClick={handleUpdateUserState} />}
            <AdminInnerLayout>
                <HospitalPageLabel onClick={() => window.location.reload()}>{AdminAssessmentPaymentConstants.pageCommonConstants.title}</HospitalPageLabel>
                <PageTitle $marginbottom='24px'>{AdminAssessmentPaymentConstants.pageCommonConstants.title}</PageTitle>
                <RowDivider $backgroundcolor={BorderColor.color_border_neutral} $marginbottom='16px' />
                <FilterWrapper>
                    <AdminAssessmentPaymentFilter
                        diagnosisNumber={totalCount}
                        afterSelectedDate={endDate}
                        setAfterSelectedDate={setEndDate}
                        beforeSelectedDate={startDate}
                        setBeforeSelectedDate={setStartDate}
                        handleCalendarDropdown={handleCalendarDropdown}
                        onBeforeCalendar={onBeforeCalendar}
                        setOnBeforeCalendar={setOnBeforeCalendar}
                        onAfterCalendar={onAfterCalendar}
                        setOnAfterCalendar={setOnAfterCalendar}
                        selectedPeriod={selectedPeriod}
                        selectedSearchFilter={selectedSearchFilter}
                        handleDropdown={handleDropdown}
                        setOnSelectedPeriodDropdown={setOnSelectedPeriodDropdown}
                        onSelectedPeriodDropdown={onSelectedPeriodDropdown}
                        setOnSearchFilterDropdown={setOnSearchFilterDropdown}
                        onSearchFilterDropdown={onSearchFilterDropdown}
                        maxDate={endDate}
                        minDate={startDate}
                        onChange={setSearchValue}
                        searchValue={searchValue}
                        handleSearchFilter={handleSearchFilter}
                        setSelectSequence={setSelectSequence}
                        toastToggle={toastToggle}
                        comment={comment}
                        afterCalenderDisabled={afterCalenderDisabled}
                        setBeforeCalendarInputWarning={setBeforeCalendarInputWarning}
                        setAfterCalendarInputWarning={setAfterCalendarInputWarning}
                        beforeCalendarInputWarning={beforeCalendarInputWarning}
                        afterCalendarInputWarning={afterCalendarInputWarning}
                        handleExcelDownload={handleExcelDownload}
                    />
                </FilterWrapper>
                <AdminAssessmentPaymentPagination
                    initNumber={paymentData?.length || 0}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageGroup={pageGroup}
                    handlePageGroup={handlePageGroup}
                    handlePageChange={setPage}
                    currentData={paymentData}
                    totalCount={totalCount} 
                    handleResetButton={handleResetButton}
                    onDataUpdate={adminAssessmentPaymentPageApiCall}
                />
            </AdminInnerLayout>
        </DiagnosisLayout>
    );
};

export default AdminAssessmentPaymentPage;
